import { useEffect, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import beaconLogoFull from 'assets/BeaconLogoFull.svg';

import { ROUTES } from 'routes/route.constants';

import { trackEvent } from 'services/eventTracking';
import { getSharedDesignReviewByToken } from 'services/teamDashboardReviews';

import styles from './styles.module.scss';

function formatDomainLabel(allowedDomain) {
  if (!allowedDomain || typeof allowedDomain !== 'string') {
    return 'work';
  }

  const part = allowedDomain.split('.')[0] || allowedDomain;

  return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
}

const SharedReviewAuthGate = () => {
  const { token } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shareContext, setShareContext] = useState(state?.shareContext ?? null);

  const creator = shareContext?.creator;
  const allowedDomain = shareContext?.allowed_domain;
  const creatorName = creator?.full_name?.trim()
    || ((creator?.username && !/none/i.test(creator.username)) ? creator.username : 'Someone');
  const profilePhoto = creator?.profile_photo;
  const domainLabel = formatDomainLabel(allowedDomain);
  const hasDomain = Boolean(allowedDomain);

  useEffect(() => {
    if (!token) {
      return;
    }

    // eslint-disable-next-line compat/compat -- IE11 not in browserslist
    const abortController = new AbortController();

    const checkAccess = async () => {
      try {
        const data = await getSharedDesignReviewByToken(token, abortController.signal);

        if (!abortController.signal.aborted) {
          // Workspace members get redirected to the full member view instead of the public/view-only page
          if (data?.requesting_user_workspace_access?.is_member && data?.uuid) {
            navigate(ROUTES.TEAM_REVIEWS_DETAIL(data.uuid), { replace: true });
          }
          else {
            navigate(ROUTES.SHARE_DESIGN_REVIEW(token), { replace: true });
          }
        }
      }
      catch (err) {
        if (err?.code === 'ERR_CANCELED') {
          return;
        }

        const responseStatus = err?.response?.status;
        const body = err?.response?.data || {};

        if (responseStatus === 401) {
          setShareContext(prev => prev ?? body?.share_context ?? null);
        }
        else {
          navigate(ROUTES.SHARE_DESIGN_REVIEW(token), { replace: true });
        }
      }
      finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      abortController.abort();
    };
  }, [token, navigate]);

  const handleContinueToSignIn = () => {
    trackEvent('[click] continue to sign in', { source: 'shared_review_auth_gate' });

    const returnTo = encodeURIComponent(`/share/${token}`);

    navigate(`${ROUTES.TEAM_LOGIN}?returnTo=${returnTo}`);
  };

  if (!token) {
    return null;
  }

  if (loading) {
    return (
      <div className={styles['loader-container']}>
        <ImpulseSpinner frontColor="#28BDAE" />
      </div>
    );
  }

  return (
    <div className={styles['page-wrapper']}>
      <div className={styles['logo-wrap']}>
        <img src={beaconLogoFull} alt="onBeacon logo" />
      </div>

      <div className={styles['page-content']}>
        <div className={styles['content-container']}>
          <div className={styles['avatar']}>
            {profilePhoto
              ? (
                <img src={profilePhoto} alt="user-profile" />
              )
              : (
                <span className={styles['avatar-initial']}>
                  {creatorName.charAt(0).toUpperCase()}
                </span>
              )}
          </div>
          <p className={styles['title-line']}>
            <span className={styles['bold-text']}>
              {creatorName}
            </span>
            {' '}
            wants to share a product audit with you
          </p>
          <p className={styles['subtitle-line']}>
            To view it, sign in using your
            {hasDomain
              ? (
                <>
                  {' '}
                  <span className={styles['bold-text']}>
                    {domainLabel}
                  </span>
                  {' '}
                  work email
                </>
              )
              : (
                ' work email'
              )}
          </p>
        </div>

        <button
          type="button"
          onClick={handleContinueToSignIn}
          className={styles['cta-button']}
        >
          Continue to Sign in
        </button>
      </div>

      <div className={styles['terms-wrap']}>
        <span>
          Read our
          {' '}
        </span>
        <a
          href={ROUTES.TERMS_AND_CONDITIONS}
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms & Conditions
        </a>
        <span>
          {' '}
          and
          {' '}
        </span>
        <a
          href={ROUTES.PRIVACY_POLICY}
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
      </div>
    </div>
  );
};

export default SharedReviewAuthGate;
